import { useEffect, useState } from "react";
import { AdminShell } from "../../components/AdminShell";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Gavel, Plus, Timer, Users as UsersIcon, X } from "lucide-react";
import { toast } from "sonner";

function fmtCountdown(ends_at) {
  if (!ends_at) return "—";
  const diff = new Date(ends_at) - new Date();
  if (diff <= 0) return "Ended";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const STATUS_STYLES = {
  scheduled: "pill-pending",
  live: "pill-available",
  ended: "pill-sold",
  cancelled: "pill-sold",
};

export default function AdminAuctions() {
  const [auctions, setAuctions] = useState([]);
  const [regs, setRegs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("active");
  const [form, setForm] = useState({
    vehicle_id: "",
    start_price: "",
    reserve_price: "",
    bid_increment: 250,
    starts_at: "",
    ends_at: "",
    description: "",
    status: "scheduled",
  });

  const load = async () => {
    const [a, r, v] = await Promise.all([
      api.get("/auctions"),
      api.get("/auction-registrations"),
      api.get("/vehicles"),
    ]);
    setAuctions(a.data || []);
    setRegs(r.data || []);
    setVehicles(v.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auctions", {
        ...form,
        start_price: Number(form.start_price),
        reserve_price: Number(form.reserve_price || 0),
        bid_increment: Number(form.bid_increment),
        starts_at: new Date(form.starts_at).toISOString(),
        ends_at: new Date(form.ends_at).toISOString(),
      });
      toast.success("Auction created");
      setOpen(false);
      setForm({ vehicle_id: "", start_price: "", reserve_price: "", bid_increment: 250, starts_at: "", ends_at: "", description: "", status: "scheduled" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/auctions/${id}`, { status });
      toast.success(`Auction ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    }
  };

  const active = auctions.filter((a) => a.status === "live" || a.status === "scheduled");
  const past = auctions.filter((a) => a.status === "ended" || a.status === "cancelled");

  return (
    <AdminShell title="Auction Management">
      <div className="flex items-center justify-between mb-6">
        <div className="flex gap-2">
          <button onClick={() => setTab("active")} className={`px-4 py-2 text-sm rounded-sm transition ${tab === "active" ? "bg-[#D4AF37] text-black font-medium" : "text-slate-300 hover:text-white"}`} data-testid="tab-active">Active ({active.length})</button>
          <button onClick={() => setTab("past")} className={`px-4 py-2 text-sm rounded-sm transition ${tab === "past" ? "bg-[#D4AF37] text-black font-medium" : "text-slate-300 hover:text-white"}`} data-testid="tab-past">Past ({past.length})</button>
          <button onClick={() => setTab("regs")} className={`px-4 py-2 text-sm rounded-sm transition ${tab === "regs" ? "bg-[#D4AF37] text-black font-medium" : "text-slate-300 hover:text-white"}`} data-testid="tab-regs">Bidder Registrations ({regs.length})</button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-cta rounded-sm" data-testid="create-auction-btn"><Plus className="w-4 h-4 mr-2" /> New Auction</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0e1a] border-white/10 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="chrome-text text-xl">Create New Auction</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2" data-testid="auction-form">
              <div>
                <Label className="text-sm">Vehicle *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="vehicle-select"><SelectValue placeholder="Select a vehicle" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0e1a] border-white/10 max-h-72">
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} — ${v.price?.toLocaleString()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-sm">Start price *</Label><Input required type="number" value={form.start_price} onChange={(e) => setForm({ ...form, start_price: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="start-price" /></div>
                <div><Label className="text-sm">Reserve</Label><Input type="number" value={form.reserve_price} onChange={(e) => setForm({ ...form, reserve_price: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="reserve-price" /></div>
                <div><Label className="text-sm">Bid increment</Label><Input type="number" value={form.bid_increment} onChange={(e) => setForm({ ...form, bid_increment: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="bid-increment" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Starts *</Label><Input required type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="starts-at" /></div>
                <div><Label className="text-sm">Ends *</Label><Input required type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} className="bg-[#04060d] border-white/10 h-11 mt-1" data-testid="ends-at" /></div>
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#04060d] border-white/10 mt-1" rows={3} data-testid="description" />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-cta rounded-sm" data-testid="submit-auction">Create Auction</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {tab !== "regs" && (
        <div className="surface rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="surface-elev">
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Vehicle</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Current bid</th>
                <th className="px-5 py-3">Bids</th>
                <th className="px-5 py-3">Time</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(tab === "active" ? active : past).map((a) => (
                <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`auction-row-${a.id}`}>
                  <td className="px-5 py-4">
                    <div className="font-medium">{a.vehicle?.year} {a.vehicle?.make} {a.vehicle?.model}</div>
                    <div className="text-xs text-slate-500">Start ${a.start_price?.toLocaleString()} · Reserve ${a.reserve_price?.toLocaleString() || "—"}</div>
                  </td>
                  <td className="px-5 py-4"><span className={`pill ${STATUS_STYLES[a.status] || "pill-pending"}`}>{a.status}</span></td>
                  <td className="px-5 py-4 gold-text font-semibold">${a.current_bid?.toLocaleString()}</td>
                  <td className="px-5 py-4">{a.bid_count || 0}</td>
                  <td className="px-5 py-4 text-xs">
                    <div className="flex items-center gap-1 text-[#f0c659]"><Timer className="w-3 h-3" /> {fmtCountdown(a.ends_at)}</div>
                    <div className="text-slate-500 mt-1">{a.ends_at ? new Date(a.ends_at).toLocaleString() : ""}</div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {a.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "live")} className="rounded-sm h-8 text-xs mr-2 border-emerald-500/40 text-emerald-400" data-testid={`go-live-${a.id}`}>Go Live</Button>}
                    {a.status === "live" && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, "ended")} className="rounded-sm h-8 text-xs mr-2 border-[#D4AF37]/40 text-[#D4AF37]" data-testid={`end-auction-${a.id}`}>End</Button>}
                    {(a.status === "live" || a.status === "scheduled") && <Button size="sm" variant="ghost" onClick={() => updateStatus(a.id, "cancelled")} className="rounded-sm h-8 text-xs text-slate-400" data-testid={`cancel-${a.id}`}><X className="w-3 h-3 mr-1" />Cancel</Button>}
                  </td>
                </tr>
              ))}
              {(tab === "active" ? active : past).length === 0 && (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-500">
                  <Gavel className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  No {tab} auctions yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "regs" && (
        <div className="surface rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="surface-elev">
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3">Phone</th>
                <th className="px-5 py-3">Dealer License</th>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {regs.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`reg-row-${r.id}`}>
                  <td className="px-5 py-3">{r.full_name}</td>
                  <td className="px-5 py-3 text-slate-300">{r.email}</td>
                  <td className="px-5 py-3 text-slate-400">{r.phone || "—"}</td>
                  <td className="px-5 py-3 text-slate-400 font-mono text-xs">{r.dealer_license || "—"}</td>
                  <td className="px-5 py-3 text-slate-400">{r.company || "—"}</td>
                  <td className="px-5 py-3"><span className="pill pill-pending">{r.status}</span></td>
                </tr>
              ))}
              {regs.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-16 text-center text-slate-500">
                  <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  No bidder registrations yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
